import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SearchService from "../services/search";

// Highlight keyword within text
function HighlightText({ text, keyword }) {
  if (!text || !keyword) return <span>{text}</span>;
  const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-black px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

export default function SearchKeyword() {
  let { keyword, lang } = useParams();

  let [results, setResults] = useState([]);
  let [totalCount, setTotalCount] = useState(0);
  let [loading, setLoading] = useState(true);
  let [searchInput, setSearchInput] = useState(keyword || "");

  useEffect(() => {
    window.scrollTo(0, 0);
    setSearchInput(keyword || "");
    getSearchData();
    // eslint-disable-next-line
  }, [keyword]);

  useEffect(() => {
    if (lang !== "th" && lang !== "en") {
      window.open(`/th`, "_self");
    }
  });

  const getSearchData = async () => {
    setLoading(true);
    try {
      let res = await SearchService.searchGlobal(keyword);
      setResults(res.results);
      setTotalCount(res.totalCount);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      window.location.href = `/${lang}/search-keyword/${encodeURIComponent(searchInput.trim())}`;
    }
  };

  // Group results by category
  const groupedResults = results.reduce((groups, item) => {
    const cat = item.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
    return groups;
  }, {});

  return (
    <div className="content-detail pt-[135px] lg:pt-[0px]">
      <div className="section">
        <div className="page-container">
          <div className="mt-[92px] mb-[46px]">

            {/* Search Box */}
            <form onSubmit={handleSearch} className="mb-[30px]">
              <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden focus-within:border-blue transition-colors">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={lang === "th" ? "ค้นหา..." : "Search..."}
                  className="w-full px-[20px] py-[14px] text-body outline-none"
                />
                <button
                  type="submit"
                  className="px-[20px] py-[14px] bg-blue text-white hover:bg-blue/90 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
              </div>
            </form>

            {/* Results Header */}
            <h1 className="text-heading font-bold text-slateGray text-left">
              {lang === "th" ? "ผลการค้นหาสำหรับ" : "Search results for"} "
              {keyword}"
            </h1>
            {!loading && (
              <p className="text-small text-darkGray mt-[8px]">
                {lang === "th"
                  ? `พบ ${totalCount} รายการ`
                  : `${totalCount} result${totalCount !== 1 ? "s" : ""} found`}
              </p>
            )}

            <div className="w-[120px] border-[1px] border-black my-[22px]" />

            {loading ? (
              <p className="text-body text-left mt-[25px]">
                {lang === "th" ? "กำลังค้นหา..." : "Searching..."}
              </p>
            ) : results.length === 0 ? (
              <h1 className="text-heading font-bold text-slateGray text-left mt-[25px]">
                {lang === "th" ? "ไม่พบข้อมูล" : "Not found"}
              </h1>
            ) : (
              <div className="flex flex-col gap-[40px]">
                {Object.entries(groupedResults).map(([category, items]) => (
                  <div key={category}>
                    <h2 className="text-lead font-semibold text-left text-blue mb-[15px]">
                      {category}
                    </h2>
                    <div className="flex flex-col gap-[20px]">
                      {items.map((item, i) => (
                        <Link
                          to={item.link}
                          key={i}
                          className="block p-[20px] bg-white border border-gray-200 rounded-lg hover:shadow-lg hover:border-blue/30 transition-all duration-300 group"
                        >
                          <h3 className="text-body text-blue group-hover:underline font-medium">
                            <HighlightText text={item.title} keyword={keyword} />
                          </h3>
                          <p className="text-tiny text-darkGray mt-[4px]">
                            {lang === "th" ? "พบใน:" : "Found in:"}{" "}
                            <span className="text-blue/70 font-medium">
                              {item.category}
                            </span>
                          </p>
                          {item.snippet && (
                            <p className="text-small text-gray-600 mt-[8px] leading-relaxed">
                              <HighlightText
                                text={item.snippet}
                                keyword={keyword}
                              />
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
